import ReactMarkdown, { type Options } from "react-markdown";
import remarkBreaks from "remark-breaks";

import { cn } from "@baluu/ui/lib/utils";

/**
 * Renders a single line of markdown with *inline* formatting only — bold,
 * italic, strikethrough, inline code and links. Block-level constructs
 * (headings, lists, blockquotes, paragraphs) are unwrapped to their text so
 * the output stays on one line and is safe to drop inside a heading, a banner
 * row, or any other inline context.
 *
 * Links open in a new tab; react-markdown sanitizes the href (e.g. strips
 * `javascript:`) by default. It also renders a React tree rather than HTML, so
 * text authored by a school can never contribute an attribute or an element of
 * its own — which is why this is the renderer for tenant-authored strings on
 * customer-facing surfaces.
 */
const INLINE_ELEMENTS = ["strong", "em", "del", "code", "a", "br"];

/** Stable identities: react-markdown rebuilds its processor when these change. */
const NO_PLUGINS: Options["remarkPlugins"] = [];
const MULTILINE_PLUGINS: Options["remarkPlugins"] = [remarkBreaks];

interface InlineMarkdownProps {
  children: string;
  className?: string;
  /**
   * Treat the text as possibly spanning several lines. Markdown collapses a
   * single newline into a space, which is right for a banner title but wrong
   * for a field the author typed line by line, so this turns each one into a
   * `<br>` and keeps blank-line-separated paragraphs on separate lines.
   * Formatting stays inline either way.
   */
  multiline?: boolean;
}

export function InlineMarkdown({
  children,
  className,
  multiline = false,
}: InlineMarkdownProps) {
  return (
    <span className={cn(className)}>
      <ReactMarkdown
        remarkPlugins={multiline ? MULTILINE_PLUGINS : NO_PLUGINS}
        allowedElements={[...INLINE_ELEMENTS, "p"]}
        unwrapDisallowed
        components={{
          // Strip the paragraph wrapper so we render inline, not as a block —
          // unless the caller asked for multiline, where two paragraphs run
          // into each other without something to separate them.
          p: ({ children }) =>
            multiline ? (
              <span className="block">{children}</span>
            ) : (
              <>{children}</>
            ),
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-4"
            >
              {children}
            </a>
          )
        }}
      >
        {children}
      </ReactMarkdown>
    </span>
  );
}
